import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import styled from "styled-components";
import { retryPendingWithdrawal, savePrivacyPreferences, usePrivacyPreferences } from "./privacyPreferences";
import { getFeatureSession, clearFeatureSession, onSessionChange } from "./featureSession";
import { recoveryRequest } from "../../utils/api";
import { PRIVACY_POLICY_SECTIONS } from "../voice/privacyPolicyContent";
import LegalDocumentLinks from "./LegalDocumentLinks";
import { appPath } from "../../utils/paths";
import { CONSENT_SCOPES, MASTER_SERVICE_SCOPES } from "./requestPolicy";

const Controls = styled.section`
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
  pointer-events: auto;
  color: #fff;
  font: 400 13px/1.45 var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif);
  @media (max-width: 760px) { width: 100%; }
`;
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  @media (max-width: 760px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    & > button { min-width: 0; padding: 8px 6px; white-space: normal; }
    & > button:only-child { grid-column: 1 / -1; }
  }
`;
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: 34px;
  padding: 7px 12px;
  border: 1px solid rgba(173,226,255,.3);
  border-radius: 10px;
  background: rgba(18,28,38,.72);
  color: #f4fbff;
  font-family: var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { background: rgba(36,56,72,.9); border-color: rgba(173,226,255,.6); }
  &:disabled { opacity: .45; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid #9fdcff; outline-offset: 3px; }
  @media (max-width: 760px) { min-height: 44px; }
`;
const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  margin: 14px 0 16px;
`;
const DataKeyCard = styled.section`
  display: grid;
  gap: 10px;
  min-width: 0;
  margin: 14px 0;
  padding: 14px;
  border: 1px solid rgba(159,220,255,.65);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(27,67,88,.55), rgba(10,20,32,.75));
  color: #fff;
  & > strong { font: 600 15px/1.3 var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif); }
  & > small { color: rgba(233,245,252,.8); font-size: 12px; line-height: 1.5; }
  ${ButtonGroup} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 0;
  }
  ${Button} { min-height: 44px; padding: 7px 8px; }
`;
const DataKeyValue = styled.code`
  display: block;
  min-width: 0;
  padding: 12px;
  border: 1px solid rgba(159,220,255,.35);
  border-radius: 8px;
  background: rgba(4,12,21,.7);
  color: #fff;
  font: 14px/1.6 ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: .04em;
  overflow-wrap: anywhere;
  user-select: all;
  -webkit-user-select: all;
  cursor: text;
  &:focus-visible { outline: 2px solid #9fdcff; outline-offset: 3px; }
`;
const ProviderLink = styled.a`
  display: inline-block;
  margin: 8px 0 12px;
  color: #9fdcff;
  font: 13px/1.5 var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif);
  text-decoration: underline;
  text-underline-offset: 3px;
  pointer-events: auto;
  animation: none;
  &:hover { color: #c7ebff; }
  &:focus-visible { outline: 2px solid #9fdcff; outline-offset: 3px; border-radius: 3px; }
`;
const ConsentReceipt = styled.div`
  display: grid;
  gap: 4px;
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(159,220,255,.65);
  border-radius: 9px;
  background: linear-gradient(110deg, rgba(27,67,88,.7), rgba(18,28,38,.72));
  color: #f4fbff;
  span { color: #bfeaff; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
  code { font: 12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace; overflow-wrap: anywhere; user-select: text; }
  small { color: rgba(233,245,252,.75); font-size: 11px; }
`;
const ScopeChoices = styled.fieldset`
  display: grid;
  gap: 8px;
  margin: 14px 0;
  padding: 12px;
  border: 1px solid rgba(173,226,255,.2);
  border-radius: 10px;
  legend { padding: 0 6px; color: #d9efff; font-size: 12px; font-weight: 600; }
  && label {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    align-items: start;
    column-gap: 9px;
    margin: 0;
    padding: 8px;
    border-radius: 8px;
    background: rgba(255,255,255,.035);
    cursor: pointer;
  }
  && input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 44px;
    min-width: 44px;
    min-height: 26px;
    height: 26px;
    margin: 0;
    padding: 0;
    font: inherit;
    box-sizing: border-box;
    border: 1px solid rgba(221,235,246,.2);
    border-radius: 999px;
    background-color: #45454a;
    background-image: radial-gradient(circle, #fff 0 9px, transparent 9.5px);
    background-size: 24px 24px;
    background-position: 0 center;
    background-repeat: no-repeat;
    transition: background-color 180ms ease, background-position 180ms ease, border-color 180ms ease, transform 130ms ease;
    &:checked {
      border-color: #30d158;
      background-color: #30d158;
      background-position: 20px center;
    }
    &:hover { border-color: rgba(221,235,246,.55); }
    &:focus-visible { outline: 2px solid #9fdcff; outline-offset: 3px; }
    &:active { transform: scale(.94); }
    &:disabled { opacity: .45; cursor: not-allowed; }
  }
  strong { display: block; color: #f4fbff; font-size: 13px; font-weight: 600; }
  small { margin-top: 3px; color: rgba(233,245,252,.72); font-size: 11px; font-weight: 400; }
`;
const AllServicesControl = styled.div`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: max-content;
`;
const AllServicesSwitch = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.4rem;
  width: max-content;
  min-height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f4fbff;
  font: 500 11px/1 var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif);
  white-space: nowrap;
  cursor: pointer;
  & > span:not([aria-hidden="true"]) { text-align: left; line-height: 1.05; }
  &:disabled { opacity: .65; cursor: wait; }
  span[aria-hidden="true"] {
    position: relative;
    width: 46px;
    height: 26px;
    flex: 0 0 46px;
    border: 1px solid rgba(221,235,246,.28);
    border-radius: 999px;
    background: #45454a;
    transition: background-color 180ms ease, border-color 180ms ease;
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      transform-origin: center;
      transition: transform 180ms ease;
    }
  }
  &[aria-checked="true"] span[aria-hidden="true"] { border-color: #30d158; background: #30d158; }
  &[aria-checked="true"] span[aria-hidden="true"]::after { transform: translateX(20px); }
  &[aria-checked="mixed"] span[aria-hidden="true"] {
    border-color: #30d158;
    background: linear-gradient(90deg, #30d158 50%, #45454a 50%);
  }
  &[aria-checked="mixed"] span[aria-hidden="true"]::after { transform: translateX(10px); }
  &[aria-checked="false"]:not(:disabled) span[aria-hidden="true"] {
    &::after { animation: full-si-dot-pulse 1.6s ease-in-out infinite; }
  }
  &:focus-visible span[aria-hidden="true"] { outline: 2px solid #9fdcff; outline-offset: 3px; }
  @keyframes full-si-dot-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(.72); opacity: .72; }
  }
  @media (prefers-reduced-motion: reduce) {
    span[aria-hidden="true"], span[aria-hidden="true"]::after { transition: none; }
    &[aria-checked="false"]:not(:disabled) span[aria-hidden="true"] {
      &::after { animation: none; }
    }
  }
`;
const MasterToggleStatus = styled.span`
  color: #f4fbff;
  font: 11px/1.4 var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif);
  ${({ $error }) => $error ? `
    display: block;
    width: 0;
    min-width: 100%;
    margin-top: 6px;
    overflow-wrap: anywhere;
  ` : `
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  `}
`;
const Panel = styled.div`
  position: ${({ $embedded }) => $embedded ? 'static' : 'absolute'};
  bottom: calc(100% + 10px);
  right: 0;
  width: ${({ $embedded }) => $embedded ? '100%' : 'min(440px, calc(100vw - 32px))'};
  box-sizing: border-box;
  padding: ${({ $embedded }) => $embedded ? '0' : '16px'};
  border: ${({ $embedded }) => $embedded ? '0' : '1px solid rgba(255,255,255,.35)'};
  border-radius: 12px;
  background: ${({ $embedded }) => $embedded ? 'transparent' : 'rgba(12,12,12,.98)'};
  max-height: ${({ $embedded }) => $embedded ? 'none' : 'min(60dvh, 480px)'};
  overflow-y: ${({ $embedded }) => $embedded ? 'visible' : 'auto'};
  box-shadow: ${({ $embedded }) => $embedded ? 'none' : '0 8px 30px #0008'};
  overflow-wrap: anywhere;
  button { max-width: 100%; white-space: normal; }
  & > button { margin: 12px 0; }
  @media (max-width: 760px) { bottom: calc(100% + 32px); }
  p { margin: 8px 0; }
  label {
    display: grid;
    gap: 8px;
    margin: 16px 0 12px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    color: #f4fbff;
  }
  input {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: 10px 12px;
    border: 1px solid rgba(173,226,255,.3);
    border-radius: 10px;
    background: rgba(18,28,38,.72);
    color: #f4fbff;
    font: 14px/1.4 var(--joz-font-family, 'JozInter', 'Inter', Arial, sans-serif);
    caret-color: #9fdcff;
    &::placeholder { color: rgba(214,234,245,.55); }
    &:hover { border-color: rgba(173,226,255,.6); }
    &:focus-visible { outline: 2px solid #9fdcff; outline-offset: 3px; }
    @media (max-width: 760px) { min-height: 44px; font-size: 16px; }
  }
  small { display: block; color: #ccc; }
  details { margin-top: 16px; }
  summary {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    align-items: center;
    column-gap: 8px;
    min-height: 44px;
    box-sizing: border-box;
    padding: 11px 6px;
    border-radius: 10px;
    color: #f4fbff;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    list-style: none;
    text-decoration: none;
    cursor: pointer;
    &::-webkit-details-marker { display: none; }
    &::marker { content: ''; }
    &::before {
      content: '';
      width: 8px;
      height: 8px;
      justify-self: center;
      align-self: center;
      margin: 0;
      box-sizing: border-box;
      border-right: 1.5px solid #9fdcff;
      border-bottom: 1.5px solid #9fdcff;
      transform: rotate(-45deg);
      transition: transform 160ms ease;
    }
    &:hover { background: rgba(173,226,255,.06); }
    &:focus-visible { outline: 2px solid #9fdcff; outline-offset: 3px; }
  }
  details[open] > summary::before { transform: translateY(-2px) rotate(45deg); }
  @media (prefers-reduced-motion: reduce) { summary::before { transition: none; } }
`;

function download(name, data) {
  const url=URL.createObjectURL(new Blob([JSON.stringify(data,null,2)],{type:"application/json"}));
  const a=document.createElement("a");
  a.href=url;
  a.download=name;
  a.hidden=true;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Safari and some mobile browsers need the object URL to stay alive after click.
  window.setTimeout(()=>URL.revokeObjectURL(url),1000);
}
function recoveryInputValue(value) {
  const input=String(value||"").trim();
  if(!input.startsWith("{")) return input;
  try {
    const parsed=JSON.parse(input);
    return typeof parsed.recoverySecret === "string" ? parsed.recoverySecret.trim() : input;
  } catch { return input; }
}

export function AllServicesToggle() {
  const preferences=usePrivacyPreferences();
  const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState("");
  const [hasError,setHasError]=useState(false);
  const pendingTarget=preferences.targetScopes;
  const pendingEnabled=pendingTarget?MASTER_SERVICE_SCOPES.every(scope=>pendingTarget[scope.key]):null;
  const actionGeneration=useRef(0);
  const hadPendingWithdrawal=useRef(false);
  useEffect(()=>{
    if(hadPendingWithdrawal.current&&!preferences.pendingWithdrawal){setHasError(false);setMessage('Server withdrawal confirmed.');}
    hadPendingWithdrawal.current=Boolean(preferences.pendingWithdrawal);
  },[preferences.pendingWithdrawal]);
  const enabledCount=MASTER_SERVICE_SCOPES.filter(scope=>preferences.scopes?.[scope.key]===true).length;
  const allEnabled=enabledCount===MASTER_SERVICE_SCOPES.length;
  const toggleLabel=pendingEnabled===true?"Enabling Full SI":allEnabled?"Full SI Enabled":"Enable Full SI";
  const displayedEnabledCount=pendingTarget?MASTER_SERVICE_SCOPES.filter(scope=>pendingTarget[scope.key]).length:enabledCount;
  const displayedPartial=displayedEnabledCount>0&&displayedEnabledCount<MASTER_SERVICE_SCOPES.length;
  const displayedState=displayedEnabledCount===MASTER_SERVICE_SCOPES.length?"true":displayedPartial?"mixed":"false";
  const action=async()=>{
    const requestGeneration=++actionGeneration.current;
    setBusy(true);setMessage("");setHasError(false);
    const enableAll=!(pendingEnabled??allEnabled);
    try {
      const scopes={...(preferences.targetScopes||preferences.scopes),...Object.fromEntries(MASTER_SERVICE_SCOPES.map(scope=>[scope.key,enableAll]))};
      const saved=await savePrivacyPreferences({scopes});
      if(requestGeneration!==actionGeneration.current)return;
      setMessage(!saved.targetScopes&&MASTER_SERVICE_SCOPES.every(scope=>saved.scopes[scope.key]===enableAll)?(enableAll?(saved.chatSaving===false?"All SI services enabled. Chat saving is waiting for the backend update.":"All listed SI services enabled."):"All SI services disabled."):"Service choices updated.");
    } catch(error) {
      if(requestGeneration!==actionGeneration.current)return;
      setHasError(true);
      setMessage(error.message||"Could not save SI service choices.");
    } finally {
      if(requestGeneration===actionGeneration.current){
        setBusy(false);
      }
    }
  };
  return <AllServicesControl>
    <AllServicesSwitch type="button" role="checkbox" title="Full SI enables chat, Voice SI, scene descriptions, assessments, contact requests and AR handoffs. Each sends the described information when used. Choose separately in Data & Privacy." aria-label={`${pendingTarget?"Saving choices; services require server confirmation. ":""}${displayedPartial?"Partially enabled; ":""}${displayedEnabledCount} of ${MASTER_SERVICE_SCOPES.length} SI services ${pendingTarget?`selected; ${enabledCount} confirmed enabled`:"enabled"}. Activate to ${(pendingEnabled??allEnabled)?"disable":"enable"} all SI services.`} aria-checked={displayedState} aria-busy={Boolean(pendingTarget)} onClick={action}>
    <span aria-hidden="true" />
    <span>{toggleLabel}</span>
    </AllServicesSwitch>
    <MasterToggleStatus role="status" aria-live="polite" aria-atomic="true" $error={hasError}>{busy?"Saving SI service choices…":message}</MasterToggleStatus>
  </AllServicesControl>;
}

export default function LoadingPrivacyControls({ embedded = false, dataOnly = false }) {
  const preferences=usePrivacyPreferences();
  const featureSession=useSyncExternalStore(onSessionChange,getFeatureSession,getFeatureSession);
  const [editing,setEditing]=useState(false);
  const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState("");
  const [recoverySecret,setRecoverySecret]=useState("");
  const [copiedKey,setCopiedKey]=useState("");
  const scopeActionGeneration=useRef(0);
  const settingsButton=useRef(null);
  const dataKeyValue=useRef(null);
  const selectDataKey=()=>{
    if(!dataKeyValue.current)return;
    const selection=window.getSelection();
    const range=document.createRange();
    range.selectNodeContents(dataKeyValue.current);
    selection?.removeAllRanges();selection?.addRange(range);
  };
  const copyDataKey=async()=>{
    const key=featureSession?.recoverySecret;
    if(!key)return;
    try {
      if(!navigator.clipboard?.writeText)throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);setMessage('Data Key copied.');
    } catch {
      dataKeyValue.current?.focus();selectDataKey();
      setMessage('Your Data Key is selected. Copy it to save it.');
    }
  };
  const action=async(fn)=>{setBusy(true);setMessage("");try{await fn();}catch(e){setMessage(e.message);}finally{setBusy(false);}};
  const toggleScope=(scope,enabled)=>{
    const requestGeneration=++scopeActionGeneration.current;
    setMessage(`Saving ${scope.label} choice…`);
    savePrivacyPreferences({scopeChanges:{[scope.key]:enabled}})
      .then(saved=>{if(requestGeneration===scopeActionGeneration.current)setMessage(!saved.targetScopes&&saved.scopes[scope.key]===enabled?`${scope.label} choice saved.`:'Service choices updated.');})
      .catch(error=>{if(requestGeneration===scopeActionGeneration.current)setMessage(`${scope.label} choice could not be saved: ${error.message||"Please retry."}`);});
  };
  const inInformationTab = embedded && !dataOnly;
  return <Controls className={inInformationTab ? "joz-llm-panel__privacy-controls" : undefined} aria-label="Privacy" onKeyDown={event=>{if(event.key==="Escape"&&!embedded){event.stopPropagation();setEditing(false);settingsButton.current?.focus();}}}>
    {(embedded||editing)&&<Panel $embedded={embedded} id={embedded?"joz-privacy-settings":"loading-privacy-settings"}>
      {inInformationTab ? <h3 className="joz-llm-panel__policy-section-title">Privacy</h3> : <strong>{dataOnly ? 'Export and deletion' : 'Privacy'}</strong>}
      {!dataOnly&&<>
      <p>Experiencing the worlds locally is available without these choices. Only the selected service may send the information described when you use it. When you enable SI chat, completed questions and replies are saved for private service-quality review for up to 30 days. Your Data Key covers export and deletion. Switching SI chat off deletes saved chats. This does not permit training. These choices do not enable journey tracking or training.</p>
      <ScopeChoices>
        <legend>Choose which SI services to enable</legend>
        {CONSENT_SCOPES.map(scope=><label key={scope.id}>
          <input type="checkbox" role="switch" checked={(preferences.targetScopes||preferences.scopes)?.[scope.key]===true} aria-busy={preferences.pendingScopes.includes(scope.key)} onChange={event=>toggleScope(scope,event.target.checked)} />
          <span><strong>{scope.label}</strong><small>{scope.description}</small></span>
        </label>)}
      </ScopeChoices>
      {preferences.targetScopes&&<p role="status">Saving choices. A service starts only after the server confirms its permission. Switching off stops new requests on this device immediately.</p>}
      {preferences.pendingWithdrawal&&<><p role="status">Disabled on this device. Server withdrawal is pending and will retry when the connection returns.</p><Button disabled={busy} onClick={()=>action(retryPendingWithdrawal)}>Retry pending withdrawal</Button></>}
      {preferences.receipts?.length>0&&<ConsentReceipt role="group" aria-label="Consent receipts"><span>Latest service choices</span>{preferences.receipts.map(receipt=><small key={receipt.id}>{CONSENT_SCOPES.find(scope=>scope.id===receipt.purpose)?.label||receipt.purpose}: {receipt.choice} · {receipt.id}</small>)}<small>Receipt numbers document choices; they are not Data Keys.</small></ConsentReceipt>}
      </>}
      {featureSession ? <>
        <DataKeyCard aria-label="Your Data Key">
          <strong>Your Data Key</strong>
          <DataKeyValue ref={dataKeyValue} tabIndex={0} onClick={selectDataKey}>{featureSession.recoverySecret}</DataKeyValue>
          <ButtonGroup>
            <Button type="button" onClick={copyDataKey}>{copiedKey===featureSession.recoverySecret?'Copied':'Copy Data Key'}</Button>
            <Button type="button" onClick={()=>download("joz-data-key.json",{recoverySecret:featureSession.recoverySecret,notice:"Keep this Data Key private. It grants access to this session’s data controls."})}>Download Data Key</Button>
          </ButtonGroup>
          <small>Copy or download your key before closing or reloading this tab. Keep it private: it opens this session’s data controls.</small>
        </DataKeyCard>
        <p>One Data Key covers every record this application retains for this session. Export includes saved feature records, consent receipts, session details and request or delivery status.</p>
        <ButtonGroup>
        <Button disabled={busy} onClick={()=>action(async()=>download("joz-data.json",await recoveryRequest("export",featureSession.recoverySecret)))}>Export my data</Button>
        <Button disabled={busy} onClick={()=>action(async()=>{const key=featureSession.recoverySecret;const stop=savePrivacyPreferences({scopes:Object.fromEntries(CONSENT_SCOPES.map(scope=>[scope.key,false]))}).catch(()=>{});const result=await recoveryRequest("delete",key);clearFeatureSession();await stop;setMessage(`Application records deleted. External and legacy review pending. Reference: ${result.jobId}`);})}>Delete session data</Button>
        </ButtonGroup>
      </> : <p>No Data Key is available in this tab. {dataOnly ? 'In the original experience tab, open Joz MAXX → ⓘ → Privacy, enable SI services, and copy or download your Data Key.' : 'Enable SI services to create a session; then your Data Key appears here.'} If you already closed or reloaded that session, use the key file you downloaded earlier.</p>}
      <details><summary>What does my key cover?</summary>
        <p>The same key covers every record this session retains across the services you enabled, including saved assessments, contact and callback requests, AR handoffs and any previously saved conversation, plus its consent receipts, session details and request or delivery status. Your export includes an inventory of the records actually retained.</p>
        <p>When you enable SI chat, completed questions and replies are saved for private service-quality review for up to 30 days. Your Data Key covers export and deletion. Switching SI chat off deletes saved chats. This does not permit training. Cloud scene request bodies and visitor journeys are not retained.</p>
        <p>Deletion removes saved feature records and revokes the session. Minimal consent and request evidence remains until its expiry. Hosting and model-provider logs, browser speech-service records, delivered email, backups and historical records require separate handling.</p>
      </details>
      <details><summary>Recover data from an earlier session</summary>
        <label>Data Key<input type="password" autoComplete="off" placeholder="Paste your Data Key or downloaded key file" value={recoverySecret} onChange={event=>setRecoverySecret(recoveryInputValue(event.target.value))} /></label>
        <p>Use your Data Key to manage all retained application records from that session. A consent receipt number is not a Data Key.</p>
        <p>Delete recovered data removes saved application records but keeps this key active so you can review the deletion reference and retained consent evidence. Delete key and data also permanently disables this Data Key. You will not be able to export that session again.</p>
        <ButtonGroup>
        <Button disabled={busy||!recoverySecret} onClick={()=>action(async()=>{const data=await recoveryRequest("export",recoverySecret);download("joz-recovered-data.json",data);setMessage("Export prepared. Check your browser downloads for joz-recovered-data.json.");})}>Export recovered data</Button>
        <Button disabled={busy||!recoverySecret} onClick={()=>action(async()=>{const result=await recoveryRequest("delete",recoverySecret);setRecoverySecret("");setMessage(`Application records deleted; external review pending. Reference: ${result.jobId}`);})}>Delete recovered data</Button>
        <Button disabled={busy||!recoverySecret} onClick={()=>action(async()=>{if(!window.confirm("Delete all saved application data and permanently disable this Data Key? You will not be able to export this session again."))return;const result=await recoveryRequest("delete-key",recoverySecret);setRecoverySecret("");setMessage(`Application data deleted and Data Key disabled. External review pending. Reference: ${result.jobId}`);})}>Delete key and data</Button>
        </ButtonGroup>
      </details>
      <p role="status">{message}</p>
      <details><summary>Privacy notice</summary>{PRIVACY_POLICY_SECTIONS.map(section=><div key={section.title} className={inInformationTab ? "joz-llm-panel__policy-section" : undefined}>{inInformationTab ? <h3 className="joz-llm-panel__policy-section-title">{section.title}</h3> : <strong>{section.title}</strong>}{section.paragraphs.map((paragraph,index)=><p key={index}>{paragraph}</p>)}</div>)}</details>
      {!dataOnly&&!inInformationTab&&<LegalDocumentLinks />}
      {!dataOnly&&!inInformationTab&&<div><ProviderLink href={appPath('/privacy/#recipients')} target="_blank" rel="noopener noreferrer">Who processes my data? (new tab)</ProviderLink></div>}
      {!embedded&&<Button onClick={()=>{setEditing(false);settingsButton.current?.focus();}}>Close</Button>}
    </Panel>}
    {!embedded&&<Actions><Button ref={settingsButton} type="button" aria-expanded={editing} aria-controls="loading-privacy-settings" onClick={()=>setEditing(!editing)}>Privacy</Button></Actions>}
  </Controls>;
}
